// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { agrupar, armarNovedades, SIN_NOVEDADES } from "../services/novedades";
import { furgonesRTO, tractoresRTO } from "../services/porteria";
import { seccion } from "../components/layout/secciones";
import Loading from "../routes/Loading";
import "./css/novedades.css";

/* Novedades: qué pasó y qué hay que hacer. Es la HOME (`/`).
 *
 * Las tarjetas se derivan de datos que ya existen (ver `services/novedades`).
 * Acá vive sólo cómo se muestran.
 *
 * ── SE VEN TODAS JUNTAS, Y ANTES NO ─────────────────────────────────────────
 *
 * Esto era un carrusel: una tarjeta grande por vez, avanzando sola cada siete
 * segundos. Tenía sentido cuando las tarjetas posibles eran dos y aparecía una.
 * Con las que se sumaron —RTO de tractores y de acoplados, documentación,
 * viajes sin cerrar, tickets y mensajes— hoy aparecen DIEZ, y para saber qué
 * hay que hacer había que esperar más de un minuto o hacer nueve clicks.
 *
 * La primera pantalla del día tiene que contestar de un vistazo. Un carrusel
 * esconde nueve décimos de la respuesta y encima se mueve solo mientras
 * alguien lee — que es la forma más rápida de que deje de mirarse.
 *
 * El orden lo pone el service y no la grilla: primero lo que vence, después lo
 * que espera respuesta. Acá sólo se dibuja.
 */

const Novedades = () => {
  const navigate = useNavigate();
  /* Del armazón, que ya los tiene para el menú. Pedirlos de nuevo acá serían
     dos consultas más en el arranque para los mismos datos. */
  const { cuentas = {}, permisos = null } = useOutletContext() || {};
  const [rto, setRto] = useState(null);
  const [rtoF, setRtoF] = useState(null);
  /* «4 frenan una salida» es un botón: deja sólo esas (Igna, 02/11). */
  const [soloFrenan, setSoloFrenan] = useState(false);

  useEffect(() => {
    let vigente = true;
    tractoresRTO()
      .then((r) => vigente && setRto(r))
      // Un error del RTO no puede dejar sin novedades a las otras tarjetas: se
      // sigue con lista vacía, que es "no sé de RTO", no "no hay RTO".
      .catch(() => vigente && setRto([]));
    /* Los acoplados van en su propia llamada y EN PARALELO, no encadenada:
       `rto_furgones()` tarda ~290 ms y esperar una para pedir la otra sería
       medio segundo de pantalla vacía. Por el mismo motivo no está adentro de
       `conteos()`, que lo pagaría todo el hub en cada carga para dibujar el
       menú. */
    furgonesRTO()
      .then((r) => vigente && setRtoF(r))
      .catch(() => vigente && setRtoF([]));
    return () => {
      vigente = false;
    };
  }, []);

  const puede = useCallback(
    (id) => {
      /* `seccion()` es la misma búsqueda que usa el resto del hub. Estaba
         copiada acá adentro —un `flatMap().find()` igualito— y con dos copias
         el día que cambie la forma del catálogo una de las dos deja de
         encontrar la sección y la tarjeta desaparece sin decir por qué. */
      const item = seccion(id);
      if (!item) return false;
      if (!item.permiso) return true;
      // `null` es "todavía no sé", y ahí se muestra: armar la pantalla vacía y
      // llenarla medio segundo después se ve como un error.
      return permisos === null || permisos.includes(item.permiso);
    },
    [permisos],
  );

  const tarjetas = useMemo(
    () =>
      /* Se espera a las DOS. Dibujar con una sola haría aparecer tarjetas de a
         tandas, y la grilla se reacomodaría sola mientras alguien lee. */
      rto === null || rtoF === null
        ? []
        : armarNovedades({
            conteos: cuentas,
            rto,
            rtoFurgones: rtoF,
            puede,
          }),
    [rto, rtoF, cuentas, puede],
  );

  if (rto === null || rtoF === null) return <Loading />;

  if (!tarjetas.length) {
    return (
      <section className="nov">
        <div className="nov-tarjeta nov-vacio">
          <h2>{SIN_NOVEDADES.titulo}</h2>
          <p>{SIN_NOVEDADES.detalle}</p>
        </div>
      </section>
    );
  }

  const urgentes = tarjetas.filter((t) => t.tono === "urgente").length;
  /* Si ya no queda ninguna que frene, el filtro no puede dejar la pantalla
     vacía: se vuelve a ver todo. */
  const filtrando = soloFrenan && urgentes > 0;
  /* El agrupado vive en el service y no acá: es la misma clase de regla que el
     orden de las tarjetas, y en el service se prueba sin montar la pantalla. */
  const bloques = agrupar(
    filtrando ? tarjetas.filter((t) => t.tono === "urgente") : tarjetas,
  );

  return (
    <section className="nov">
      {/* CUÁNTAS SON, ARRIBA DE TODO. Con diez tarjetas, entrar y encontrarse
          una grilla no dice si el día viene tranquilo o pesado; el conteo sí,
          y se lee antes de mirar ninguna. */}
      <p className="nov-resumen">
        <b>{tarjetas.length}</b>
        {tarjetas.length === 1 ? " cosa para mirar" : " cosas para mirar"}
        {urgentes > 0 && (
          <>
            {" · "}
            {/* EL NÚMERO ES EL FILTRO. «4 frenan una salida» sin poder ver
                cuáles obligaba a buscarlas una por una entre diez. */}
            <button
              type="button"
              className={
                "nov-resumen-filtro" + (filtrando ? " nov-resumen-activo" : "")
              }
              aria-pressed={filtrando}
              onClick={() => setSoloFrenan((v) => !v)}
            >
              <b className="nov-resumen-urgente">{urgentes}</b>
              {urgentes === 1 ? " frena una salida" : " frenan una salida"}
            </button>
            {filtrando && (
              <>
                {" · "}
                <button
                  type="button"
                  className="nov-resumen-todas"
                  onClick={() => setSoloFrenan(false)}
                >
                  Ver todas
                </button>
              </>
            )}
          </>
        )}
      </p>

      {/* AGRUPADAS POR SECTOR, y con el mismo vocabulario que el menú. Diez
          tarjetas planas hacen que los 107 RTO vencidos y tus 3 tickets pesen
          lo mismo, y el que abre a la mañana no sabe cuál es suyo.

          LOS BLOQUES NO SE PLIEGAN, a propósito: una home donde hay que abrir
          algo para ver qué pasa deja de contestar de un vistazo, que es lo
          único que tiene que hacer. */}
      {bloques.map((b) => (
        <section className="nov-bloque" key={b.clave}>
          <h2 className="nov-bloque-tit">{b.titulo}</h2>
          <div className="nov-grilla">
            {b.tarjetas.map((t) => (
              <article key={t.id} className={"nov-tarjeta nov-" + t.tono}>
                <div className="nov-cuerpo">
                  <h3>{t.titulo}</h3>
                  {/* LO QUE CAMBIÓ, al lado del total. Hay 107 RTO vencidos y
                      20 se vencieron esta semana: sin el segundo número la
                      tarjeta dice lo mismo todos los días y se deja de
                      mirar. */}
                  {t.nuevos > 0 && (
                    <p className="nov-nuevos">{t.nuevos} esta semana</p>
                  )}
                  <p>{t.detalle}</p>
                  <button
                    className="nov-accion"
                    onClick={() => navigate(t.ruta)}
                  >
                    {t.accion}
                  </button>
                </div>
                <div className="nov-numero" aria-hidden="true">
                  {t.cuanto}
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </section>
  );
};

export default Novedades;
